import {DropdownMenu, IconButton} from '@wandb/components';
import React, {memo, useState} from 'react';

import {Analytics} from '../../../services/analytics';
import {DD_PRIVACY_ALLOW} from '../../../util/datadog/privacy';
import {keyDisplayName} from '../../../util/runs';
import {useRunSelectorAnalyticsLocation} from '../RunSelectorAnalyticsLocationContext';
import {useBaselineRun} from '../RunSelectorRunsContext';
import {useTableSettings} from '../RunSelectorTableSettingsContext';
import type {WBTableColumn} from '../WBTable/types';
import {
  AggregationGroup,
  columnMightHaveAggregations,
} from './AggregationGroup';
import {useFieldHasAggregations} from './AggregationGroup.queries';
import {Interpose} from './Interpose';
import {MetricValenceGroup} from './MetricValenceGroup';
import {PinningGroup} from './PinningGroup';
import {RunColorsGroup, useShowRunColors} from './RunColorsGroup';
import {RunNameTruncationGroup} from './RunNameTruncationGroup';
import {ColumnSortingOptions, HIDE_COLUMN_SORT_CONTROLS} from './SortingGroup';

type ColumnHeaderMenuProps = {
  column: WBTableColumn;
};

function ColumnHeaderMenuComp({column}: ColumnHeaderMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const analyticsLocation = useRunSelectorAnalyticsLocation();
  const baselineRun = useBaselineRun();
  const tableSettings = useTableSettings();
  const showRunColorsSection = useShowRunColors(column);
  const mayHaveAggs = columnMightHaveAggregations(column);

  // Don't fetch aggregations if the menu hasn't been opened yet, and only
  // summary metrics even get aggregations, so don't fetch if the column belongs
  // to another section:
  const skipAggregations = !isOpen || !mayHaveAggs;
  const {data, loading} = useFieldHasAggregations(
    column.key.name,
    skipAggregations
  );

  const isDisplayNameColumn = column.accessor === 'run:displayName';
  const showPinningSection = !isDisplayNameColumn;
  const showSortingSection = !HIDE_COLUMN_SORT_CONTROLS.includes(
    column.displayName ?? ''
  );
  const columnDisplayName = keyDisplayName(column.key);

  // Metric valence visibility: showMetricDeltas=false means explicitly hidden;
  // undefined means "default on" (show if a baseline run is selected)
  const deltaMetricsHiddenBySettings = tableSettings.showMetricDeltas === false;
  const showingDeltaMetrics = baselineRun && !deltaMetricsHiddenBySettings;
  const showValenceSection =
    column.key.section === 'summary' && Boolean(showingDeltaMetrics);
  const showTruncation = isDisplayNameColumn;

  return (
    <DropdownMenu.Trigger isOpen={isOpen} onOpenChange={setIsOpen}>
      <IconButton
        icon="overflow-horizontal"
        variant="ghost"
        aria-label={`${columnDisplayName} column actions`}
        data-dd-action-name="More column actions"
        data-dd-privacy={DD_PRIVACY_ALLOW}
        size="sm"
        onPress={() => {
          Analytics.track('Column header dropdown clicked', {
            location: analyticsLocation,
          });
        }}
      />
      <DropdownMenu.Content>
        <Interpose separator={<DropdownMenu.Separator />}>
          {showPinningSection ? <PinningGroup column={column} /> : null}
          {showSortingSection ? <ColumnSortingOptions column={column} /> : null}
          {showRunColorsSection ? <RunColorsGroup /> : null}
          {showTruncation ? <RunNameTruncationGroup column={column} /> : null}
          {mayHaveAggs && (data || loading) ? (
            <AggregationGroup column={column} isDisabled={loading} />
          ) : null}
          {showValenceSection ? <MetricValenceGroup column={column} /> : null}
        </Interpose>
      </DropdownMenu.Content>
    </DropdownMenu.Trigger>
  );
}

export const ColumnHeaderMenu = memo(ColumnHeaderMenuComp);
